import type {
  Node,
  TextNodeData,
  ImageNodeData,
  IframeNodeData,
} from '../types';
import { logger } from './logger';

/**
 * API node format (snake_case from Rails backend)
 */
interface ApiNode {
  id: number;
  node_type: 'text' | 'image' | 'video' | 'iframe';
  position?: { x: number; y: number };
  size?: { width: number | 'auto'; height: number | 'auto' };
  z_index?: number;
  content?: string;
  source_url?: string;
  media_url?: string;
  display_url?: string;
  title?: string;
  frame_style?: string;
  style?: Record<string, any>;
  sender_name?: string;
  avatar_url?: string;
  avatar_emoji?: string;
  background_color?: string;
  work_in_progress?: boolean;
  border_radius?: number;
  wip_banner_offset?: number;
  iframe_zoom?: number;
  has_media?: boolean;
}

/**
 * Converts API node format to frontend Node format
 */
export function apiNodeToFrontend(node: ApiNode): Node {
  if (node.node_type === 'text') {
    return {
      id: `node-${node.id}`,
      type: 'text',
      position: node.position || { x: 50, y: 50 },
      size: node.size || { width: 'auto', height: 'auto' },
      zIndex: node.z_index || 1,
      data: {
        content: node.content || '',
        frameStyle: (node.frame_style || 'browser') as TextNodeData['frameStyle'],
        style: node.style || {},
        senderName: node.sender_name,
        avatarUrl: node.avatar_url,
        avatarEmoji: node.avatar_emoji,
        backgroundColor: node.background_color,
        workInProgress: node.work_in_progress,
        borderRadius: node.border_radius,
        wipBannerOffset: node.wip_banner_offset
      } as TextNodeData
    };
  }

  if (node.node_type === 'iframe') {
    return {
      id: `node-${node.id}`,
      type: 'iframe',
      position: node.position || { x: 50, y: 50 },
      size: node.size || { width: 95, height: 85 },
      zIndex: node.z_index || 1,
      data: {
        content: node.source_url || '',
        title: node.title || '',
        frameStyle: (node.frame_style || 'browser') as 'browser' | 'none',
        workInProgress: node.work_in_progress,
        borderRadius: node.border_radius,
        wipBannerOffset: node.wip_banner_offset,
        iframeZoom: node.iframe_zoom || 1
      } as IframeNodeData
    };
  }

  // Image or video node
  const imageUrl = node.media_url || node.display_url || node.source_url || '';
  logger.debug('render', `Loading image node ${node.id}`, {
    rawNode: node,
    mediaUrl: node.media_url,
    displayUrl: node.display_url,
    sourceUrl: node.source_url,
    hasMedia: node.has_media,
    finalUrl: imageUrl
  });

  return {
    id: `node-${node.id}`,
    type: node.node_type as 'image' | 'video',
    position: node.position || { x: 50, y: 50 },
    size: node.size || { width: 'auto', height: 'auto' },
    zIndex: node.z_index || 1,
    data: {
      content: imageUrl,
      title: node.title || '',
      frameStyle: (node.frame_style || 'browser') as 'browser' | 'none',
      workInProgress: node.work_in_progress,
      borderRadius: node.border_radius,
      wipBannerOffset: node.wip_banner_offset
    } as ImageNodeData
  };
}

/**
 * Converts frontend node updates to API payload format
 */
export function nodeUpdatesToApiPayload(updates: {
  position?: { x: number; y: number };
  size?: { width: number | 'auto'; height: number | 'auto' };
  zIndex?: number;
  data?: Partial<TextNodeData> | Partial<ImageNodeData> | Partial<IframeNodeData>;
}): Record<string, any> {
  const payload: Record<string, any> = {};

  if (updates.position) payload.position = updates.position;
  if (updates.size) payload.size = updates.size;
  if (updates.zIndex !== undefined) payload.z_index = updates.zIndex;

  if (updates.data) {
    const dataUpdates = updates.data;
    if ('frameStyle' in dataUpdates) payload.frame_style = dataUpdates.frameStyle;
    if ('content' in dataUpdates) payload.content = dataUpdates.content;
    if ('title' in dataUpdates) payload.title = dataUpdates.title;
    if ('style' in dataUpdates) payload.style = dataUpdates.style;
    if ('senderName' in dataUpdates) payload.sender_name = dataUpdates.senderName;
    if ('avatarUrl' in dataUpdates) payload.avatar_url = dataUpdates.avatarUrl;
    if ('avatarEmoji' in dataUpdates) payload.avatar_emoji = dataUpdates.avatarEmoji;
    if ('backgroundColor' in dataUpdates) payload.background_color = dataUpdates.backgroundColor;
    if ('workInProgress' in dataUpdates) payload.work_in_progress = dataUpdates.workInProgress;
    if ('borderRadius' in dataUpdates) payload.border_radius = dataUpdates.borderRadius;
    if ('wipBannerOffset' in dataUpdates) payload.wip_banner_offset = dataUpdates.wipBannerOffset;
    if ('iframeZoom' in dataUpdates) payload.iframe_zoom = dataUpdates.iframeZoom;
  }

  return payload;
}

/**
 * Extracts numeric node ID from node-xxx format
 */
export function extractNodeId(nodeId: string): string {
  return nodeId.replace('node-', '');
}
